import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { API } from "@/App";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [language, setLanguage] = useState("es");

  // Detect browser language
  useEffect(() => {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.includes("ca")) {
      setLanguage("ca");
    }
  }, []);

  const t = (es, ca) => language === "ca" ? ca : es;

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } catch (error) {
        console.error("Token verification error:", error);
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t("Las contraseñas no coinciden", "Les contrasenyes no coincideixen"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("La contraseña debe tener al menos 6 caracteres", "La contrasenya ha de tenir almenys 6 caràcters"));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(t("¡Contraseña actualizada!", "Contrasenya actualitzada!"));
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error(error.response?.data?.detail || t("Error al restablecer la contraseña", "Error en restablir la contrasenya"));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
        <div className="glass rounded-3xl p-8 shadow-2xl border border-teal-400/20 text-center">
          <Loader2 className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">{t("Verificando enlace...", "Verificant enllaç...")}</p>
        </div>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-red-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="glass rounded-3xl p-8 shadow-2xl border border-red-400/20 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-red-400 to-red-600 mb-6">
              <XCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              {t("Enlace inválido o expirado", "Enllaç invàlid o caducat")}
            </h2>
            
            <p className="text-gray-400 mb-6">
              {t(
                "Este enlace de restablecimiento no es válido o ha expirado. Por favor, solicita uno nuevo.",
                "Aquest enllaç de restabliment no és vàlid o ha caducat. Si us plau, sol·licita'n un de nou."
              )}
            </p>

            <Link 
              to="/forgot-password"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t("Solicitar nuevo enlace", "Sol·licitar nou enllaç")}
            </Link>

            <div className="mt-4">
              <Link 
                to="/auth"
                className="text-gray-400 hover:text-teal-300 transition-colors text-sm"
              >
                {t("Volver al inicio de sesión", "Tornar a l'inici de sessió")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-green-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="glass rounded-3xl p-8 shadow-2xl border border-green-400/20 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              {t("¡Contraseña actualizada!", "Contrasenya actualitzada!")}
            </h2>
            
            <p className="text-gray-400 mb-6">
              {t(
                "Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.",
                "La teva contrasenya s'ha restablert correctament. Ja pots iniciar sessió amb la teva nova contrasenya."
              )}
            </p>

            <Link 
              to="/auth"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t("Iniciar sesión", "Iniciar sessió")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-teal-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-slate-400/10 rounded-full blur-3xl bottom-1/4 right-1/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass rounded-3xl p-8 shadow-2xl border border-teal-400/20">
          <Link 
            to="/auth" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("Volver", "Tornar")}
          </Link>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {t("Nueva contraseña", "Nova contrasenya")}
            </h2>
            <p className="text-gray-400 text-sm">
              {tokenEmail}
            </p>
          </div>

          <form data-testid="reset-password-form" onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                {t("Nueva contraseña", "Nova contrasenya")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t("Mínimo 6 caracteres", "Mínim 6 caràcters")}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                {t("Confirmar contraseña", "Confirmar contrasenya")}
              </label>
              <input
                id="confirmPassword"
                data-testid="confirm-password-input"
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                placeholder="••••••••"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">
                  {t("Las contraseñas no coinciden", "Les contrasenyes no coincideixen")}
                </p>
              )}
            </div>

            <button
              data-testid="submit-btn"
              type="submit"
              disabled={loading || password !== confirmPassword}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t("Actualizando...", "Actualitzant...") : t("Actualizar contraseña", "Actualitzar contrasenya")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
